import React, { Suspense, lazy } from 'react';
import { useLocation, Outlet } from 'react-router-dom';
import UnauthorizedPage from '@/components/unauthorized';
import AccountExpiredPage from '@/components/accountExpired';
import { useRoutePermission, RouteRenderType } from '@/hook/useRoutePermission';
import useStore from 'src/store';
import PageLoading from 'src/components/loading/pageLoading';

interface RouteRendererProps {
  className?: string;
}

const componentCache = new Map<string, React.LazyExoticComponent<any>>();

const STATIC_ROUTE_PATHS = new Set(['/cate', '/bestSell', '/user/cate', '/alibaba']);

function getOrCreateLazyComponent(path: string) {
  if (!componentCache.has(path)) {
    const LazyComp = lazy(() =>
      import(`src/views${path}`).catch(() => {
        return import('src/pages/404');
      })
    );
    componentCache.set(path, LazyComp);
  }
  return componentCache.get(path)!;
}

const RouteRenderer: React.FC<RouteRendererProps> = ({ className }) => {
  const location = useLocation();
  const { getRouteRenderType } = useRoutePermission();
  const { tabList } = useStore();
  const pathname = location.pathname;

  const renderType = getRouteRenderType;

  if (renderType === RouteRenderType.ACCOUNT_EXPIRED) {
    return <AccountExpiredPage />;
  }
  if (renderType === RouteRenderType.UNAUTHORIZED) {
    return <UnauthorizedPage />;
  }

  const hasCurrentTab = tabList?.some((tab) => tab.itemKey === pathname);
  const isStaticRoute = STATIC_ROUTE_PATHS.has(pathname);
  if (!tabList || tabList.length === 0 || !hasCurrentTab || isStaticRoute) {
    return (
      <Suspense fallback={<PageLoading />}>
        <Outlet />
      </Suspense>
    );
  }

  return (
    <>
      {tabList.map((tab) => {
        const isActive = pathname === tab.itemKey;
        const Component = getOrCreateLazyComponent(tab.itemKey);
        return (
          <div
            key={tab.itemKey}
            style={{
              display: isActive ? 'block' : 'none',
              height: '100%',
              overflow: 'auto',
            }}
          >
            <Suspense fallback={<PageLoading />}>
              <Component />
            </Suspense>
          </div>
        );
      })}
    </>
  );
};

export default RouteRenderer;
